import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowRight, ExternalLink } from 'lucide-react';

export function ZohoApps() {
  const zohoApps = [
    {
      name: "Zoho CRM",
      logo: "/assets/zoho-products/zoho-crm.png",
      description: "Customer relationship management platform to manage leads, contacts, and sales pipeline",
      category: "Sales & Marketing",
      color: "bg-white",
      hasOwnBackground: true
    },
    {
      name: "Zoho One",
      logo: "/assets/zoho-products/zoho-one.png",
      description: "Complete business suite with 45+ integrated applications for every business need",
      category: "Business Suite",
      color: "bg-white",
      hasOwnBackground: true
    },
    {
      name: "Zoho Books",
      logo: "/assets/zoho-products/zoho-books.png",
      description: "Online accounting software to manage finances, invoicing, and business expenses",
      category: "Finance",
      color: "bg-white",
      hasOwnBackground: true
    },
    {
      name: "Zoho Analytics",
      logo: "/assets/zoho-products/zoho-analytics.png",
      description: "Business intelligence and analytics platform for data-driven decision making",
      category: "Analytics",
      color: "bg-white",
      hasOwnBackground: true
    },
    {
      name: "Zoho Desk",
      logo: "/assets/zoho-products/zoho-desk.png",
      description: "Customer service software with ticketing, knowledge base, and multichannel support",
      category: "Customer Service",
      color: "bg-white",
      hasOwnBackground: true
    },
    {
      name: "Zoho Creator",
      logo: "/assets/zoho-products/zoho-creator.png",
      description: "Low-code platform to build custom business applications without coding",
      category: "Development",
      color: "bg-white",
      isCertified: true,
      hasOwnBackground: true
    },
    {
      name: "Zoho Inventory",
      logo: "/assets/zoho-products/zoho-inventory.png",
      description: "Inventory management software for tracking stock, orders, and warehouse operations",
      category: "Operations",
      color: "bg-white",
      hasOwnBackground: true
    },
    {
      name: "Zoho Flow",
      logo: "/assets/zoho-products/zoho-flow.png",
      description: "Integration platform to connect and automate workflows between applications",
      category: "Automation",
      color: "bg-white"
    },
    {
      name: "Zoho Commerce",
      logo: "/assets/zoho-products/zoho-commerce.png",
      description: "E-commerce platform to build and manage online stores with integrated features",
      category: "E-commerce",
      color: "bg-white",
      hasOwnBackground: true
    },
    {
      name: "Zoho People",
      logo: "/assets/zoho-products/zoho-people.png",
      description: "HR management system for employee records, attendance, and performance tracking",
      category: "Human Resources",
      color: "bg-white",
      hasOwnBackground: true
    },
    {
      name: "Zoho Projects",
      logo: "/assets/zoho-products/zoho-projects.png",
      description: "Project management software for planning, tracking, and collaborating on projects",
      category: "Project Management",
      color: "bg-white"
    },
    {
      name: "Zoho Mail",
      logo: "/assets/zoho-products/zoho-mail.png",
      description: "Business email hosting with collaboration tools and security features",
      category: "Communication",
      color: "bg-white",
      hasOwnBackground: true
    }
  ];

  const categories = Array.from(new Set(zohoApps.map(app => app.category)));

  // Toggle to show/hide 'Learn More' links for all services
  const showLearnMore = false;

  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <Badge className="mb-4 bg-blue-100 text-blue-800">Zoho Ecosystem</Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Complete Business Solutions with Zoho Applications
          </h2>
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
            We specialize in implementing and integrating the entire Zoho ecosystem 
            to create seamless business workflows and maximize productivity.
          </p>
        </div>

        {/* Zoho Creator Certification Highlight */}
        <div className="mb-12 lg:mb-16">
          <Card className="bg-gradient-to-r from-gray-800 to-gray-900 text-white border-0 relative overflow-hidden">
            <CardContent className="p-6 lg:p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="text-center lg:text-left">
                  <h3 className="text-xl lg:text-2xl font-bold mb-4">
                    Zoho Creator Registered Partner
                  </h3>
                  <p className="text-gray-300 mb-6 text-sm lg:text-base">
                    As a Zoho Creator Registered Low-Code Solution Provider, we specialize in building 
                    custom business applications that streamline your unique workflows and processes.
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl lg:text-3xl font-bold text-yellow-400">50+</div>
                      <div className="text-gray-300 text-sm">Custom Apps Built</div>
                    </div>
                    <div>
                      <div className="text-2xl lg:text-3xl font-bold text-yellow-400">Expert</div>
                      <div className="text-gray-300 text-sm">Low-Code Development</div>
                    </div>
                  </div>
                </div>
                <div className="text-center relative">
                  {/* Glowing background for badge */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-48 h-48 rounded-full bg-yellow-400 opacity-30 blur-2xl"></div>
                  </div>
                  {/* Simple badge display without container */}
                  <div className="relative inline-block">
                    <img
                      src="/assets/logos/zoho-creator-registered-badge.png"
                      alt="Zoho Creator Registered Partner Badge"
                      className="h-36 lg:h-44 w-auto mx-auto drop-shadow-2xl"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.insertAdjacentHTML('afterend', '<div class="h-36 lg:h-44 w-auto mx-auto flex items-center justify-center bg-yellow-500 text-black font-bold rounded-lg text-center px-8 py-12">CREATOR<br>REGISTERED<br>PARTNER</div>');
                      }}
                    />
                  </div>
                </div>
              </div>
              {/* Background pattern for extra visual appeal */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-yellow-600/10 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-600/10 to-transparent rounded-full blur-3xl"></div>
            </CardContent>
          </Card>
        </div>

        {/* Zoho Product Logos Ticker */}
        <div className="mb-10"></div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 lg:gap-4 mb-8 lg:mb-12">
          {categories.map((category, index) => (
            <Badge 
              key={index} 
              variant="outline" 
              className="cursor-pointer hover:bg-blue-50 transition-colors text-xs lg:text-sm"
            >
              {category}
            </Badge>
          ))}
        </div>

        {/* Zoho Apps Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8 mb-12 lg:mb-16">
          {zohoApps.map((app, index) => (
            <Card key={index} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 relative">
              <CardContent className="p-4 lg:p-6">
                {app.isCertified && (
                  <div className="absolute -top-2 -right-2 bg-yellow-400 text-black text-xs font-bold px-2 py-1 rounded-full">
                    CERTIFIED
                  </div>
                )}
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-2 rounded-lg ${app.color} flex items-center justify-center`}>
                    <img
                      src={app.logo}
                      alt={`${app.name} logo`}
                      className="h-10 w-10 lg:h-12 lg:w-12 object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm lg:text-base">{app.name}</h3>
                    <Badge variant="secondary" className="text-xs">
                      {app.category}
                    </Badge>
                  </div>
                </div>
                
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {app.description}
                </p>
                
                {/* Learn More button - hidden for now, keep for future use */}
                {showLearnMore && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full group-hover:bg-blue-50 transition-colors text-xs lg:text-sm"
                  >
                    Learn More
                    <ExternalLink className="ml-2 h-3 w-3 lg:h-4 lg:w-4" />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Implementation CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 lg:p-8 text-white text-center">
          <h3 className="text-xl lg:text-2xl font-bold mb-4">
            Ready to Transform Your Business with Zoho?
          </h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto text-sm lg:text-base">
            Our certified experts will help you choose the right Zoho applications 
            and create a customized implementation plan for your business needs.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8 mb-6 lg:mb-8">
            <div className="text-center">
              <div className="text-2xl lg:text-3xl font-bold">45+</div>
              <div className="text-blue-200 text-sm lg:text-base">Zoho Applications</div>
            </div>
            <div className="text-center">
              <div className="text-2xl lg:text-3xl font-bold">500+</div>
              <div className="text-blue-200 text-sm lg:text-base">Successful Implementations</div>
            </div>
            <div className="text-center">
              <div className="text-2xl lg:text-3xl font-bold">Expert</div>
              <div className="text-blue-200 text-sm lg:text-base">Zoho Consultants</div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="secondary" size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              Schedule Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10">
              View Pricing
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}